import React, { useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Upload,
  Switch,
  InputNumber,
  Image,
  Popover,
  Statistic,
  Row,
  Col,
  App,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  StockOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import { getStorageUrl } from '../utils/storageUtils';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Products = () => {
  // Get modal and message from App context
  const { modal, message } = App.useApp();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [isStockModalVisible, setIsStockModalVisible] = useState(false);
  const [stockProduct, setStockProduct] = useState(null);
  const [form] = Form.useForm();
  const [stockForm] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [imageFileList, setImageFileList] = useState([]);
  const queryClient = useQueryClient();

  // Fetch products with filters and pagination
  const {
    data: productsResponse = { data: [], total: 0, current_page: 1, last_page: 1, per_page: 10 },
    isLoading,
  } = useQuery(
    ['products', searchTerm, categoryFilter, typeFilter, page, perPage],
    async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter) params.append('category_id', categoryFilter);
      if (typeFilter) params.append('type', typeFilter);
      params.append('page', page);
      params.append('per_page', perPage);

      const response = await api.get(`/admin/products-services?${params.toString()}`);
      const responseData = response.data;

      // Extract products data and ensure images are preserved
      if (responseData?.data && Array.isArray(responseData.data)) {
        return {
          data: responseData.data,
          total: responseData.total || 0,
          current_page: responseData.current_page || 1,
          last_page: responseData.last_page || 1,
          per_page: responseData.per_page || perPage,
        };
      } else if (Array.isArray(responseData)) {
        return {
          data: responseData,
          total: responseData.length,
          current_page: 1,
          last_page: 1,
          per_page: responseData.length,
        };
      }
      return { data: [], total: 0, current_page: 1, last_page: 1, per_page: perPage };
    },
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        message.success(`Products loaded: ${data.total} items`);
      },
      onError: (error) => {
        console.error('Products query error:', error);
        message.error('Failed to load products');
      },
    },
  );

  const products = productsResponse.data;
  const totalProducts = productsResponse.total;

  // Client-side filtering fallback
  const filteredProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];
    let filtered = [...products];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(searchLower)) ||
          (p.description && p.description.toLowerCase().includes(searchLower)) ||
          (typeof p.category === 'string' && p.category.toLowerCase().includes(searchLower)) ||
          (p.category?.name && p.category.name.toLowerCase().includes(searchLower)) ||
          (typeof p.brand === 'string' && p.brand.toLowerCase().includes(searchLower)) ||
          (p.brand?.name && p.brand.name.toLowerCase().includes(searchLower)),
      );
    }

    return filtered;
  }, [products, searchTerm]);

  // Fetch categories for dropdown
  const { data: categories = [] } = useQuery('categories', async () => {
    const response = await api.get('/admin/categories');
    return response.data?.data || response.data || [];
  });

  // Fetch brands for dropdown
  const { data: brands = [] } = useQuery('brands', async () => {
    const response = await api.get('/admin/brands');
    return response.data?.data || response.data || [];
  });

  // Fetch providers for dropdown
  const { data: providers = [] } = useQuery('admin-providers', async () => {
    const response = await api.get('/admin/providers');
    return response.data?.data || response.data || [];
  });

  // Create product mutation
  const createProductMutation = useMutation(
    async (productData) => {
      const formData = new FormData();

      // Append all form fields
      Object.keys(productData).forEach((key) => {
        if (key !== 'tags' && productData[key] !== null && productData[key] !== undefined) {
          formData.append(key, productData[key]);
        }
      });

      // Handle tags separately
      if (productData.tags) {
        const tagsArray = Array.isArray(productData.tags) ? productData.tags : [];
        formData.append('tags', JSON.stringify(tagsArray));
      }

      // Handle images from file list
      if (Array.isArray(imageFileList)) {
        imageFileList.forEach((file, index) => {
          if (file.originFileObj) {
            formData.append(`images[${index}]`, file.originFileObj);
          }
        });
      }

      const response = await api.post('/admin/products-services', formData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Product created successfully');
        setIsModalVisible(false);
        setEditingProduct(null);
        setImageFileList([]);
        form.resetFields();
        queryClient.invalidateQueries('products');
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to create product';
        message.error(errorMessage);
      },
    },
  );

  // Update product mutation
  const updateProductMutation = useMutation(
    async ({ id, ...values }) => {
      const formData = new FormData();

      // Add all form fields
      Object.keys(values).forEach((key) => {
        if (key !== 'tags' && values[key] !== undefined && values[key] !== null) {
          if (typeof values[key] === 'object') {
            formData.append(key, JSON.stringify(values[key]));
          } else {
            formData.append(key, values[key]);
          }
        }
      });

      // Handle tags separately
      if (values.tags) {
        const tagsArray = Array.isArray(values.tags) ? values.tags : [];
        formData.append('tags', JSON.stringify(tagsArray));
      }

      // Track existing image URLs to preserve them on the server
      let existingImageIndex = 0;
      if (Array.isArray(imageFileList)) {
        imageFileList.forEach((file) => {
          if (file.originFileObj) {
            // New image - send the file
            formData.append(`images[${existingImageIndex}]`, file.originFileObj);
            existingImageIndex++;
          } else if (file.url) {
            // Existing image - send URL so backend keeps it
            formData.append(`existing_images[${existingImageIndex}]`, file.url);
            existingImageIndex++;
          }
        });
      }

      // Add method override for PUT
      formData.append('_method', 'PUT');

      const response = await api.post(`/admin/products-services/${id}`, formData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Product updated successfully');
        setIsModalVisible(false);
        setEditingProduct(null);
        setImageFileList([]);
        form.resetFields();

        // Force refresh the products data
        queryClient.invalidateQueries('products');

        // Also refetch immediately to ensure latest data
        setTimeout(() => {
          queryClient.refetchQueries('products');
        }, 500);
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to update product';
        message.error(errorMessage);
      },
    },
  );

  // Update stock mutation
  const updateStockMutation = useMutation(
    async ({ id, quantity, operation }) => {
      const response = await api.put(`/admin/products-services/${id}/stock`, {
        quantity,
        operation,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Stock updated successfully');
        setIsStockModalVisible(false);
        setStockProduct(null);
        stockForm.resetFields();
        queryClient.invalidateQueries('products');
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.error || error.response?.data?.message || 'Failed to update stock';
        message.error(errorMessage);
      },
    },
  );

  const handleView = (record) => {
    setViewingProduct(record);
    setIsViewModalVisible(true);
  };

  const handleStockManagement = (record) => {
    setStockProduct(record);
    stockForm.setFieldsValue({
      current_stock: record.stock_quantity || 0,
      operation: 'set',
      quantity: record.stock_quantity || 0,
    });
    setIsStockModalVisible(true);
  };

  const handleStockUpdate = (values) => {
    updateStockMutation.mutate({
      id: stockProduct.id,
      quantity: values.quantity,
      operation: values.operation,
    });
  };

  const handleEdit = (record) => {
    setEditingProduct(record);
    form.setFieldsValue({
      ...record,
      price: parseFloat(record.price || 0),
      cost_price: parseFloat(record.cost_price || 0),
      stock_quantity: parseInt(record.stock_quantity || 0),
      discount:
        record.discount_type === 'percentage' ? parseFloat(record.discount_value || 0) : null,
      tags: record.tags
        ? typeof record.tags === 'string'
          ? JSON.parse(record.tags)
          : record.tags
        : [],
    });
    // Load existing images into file list
    if (record.images && Array.isArray(record.images) && record.images.length > 0) {
      const existingFiles = record.images.map((img, index) => {
        const imgUrl = typeof img === 'string' ? img : img.url || img.image_url;
        return {
          uid: `existing-${index}`,
          name: imgUrl || `image-${index}`,
          status: 'done',
          url: getStorageUrl(imgUrl),
        };
      });
      setImageFileList(existingFiles);
    } else {
      setImageFileList([]);
    }
    setIsModalVisible(true);
  };

  const handleDelete = (record) => {
    modal.confirm({
      title: 'Are you sure you want to delete this product?',
      content: `Product: ${record.name}`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await api.delete(`/admin/products-services/${record.id}`);

          if (response.status === 204) {
            message.success('Product deleted successfully');
            queryClient.invalidateQueries('products');
          } else {
            message.error('Failed to delete product');
          }
        } catch (error) {
          console.error('Delete error:', error);
          message.error('Failed to delete product');
        }
      },
    });
  };

  const handleSubmit = (values) => {
    if (typeof values.discount === 'number' && values.discount > 0) {
      values.discount_type = 'percentage';
      values.discount_value = values.discount;
    } else {
      values.discount_type = 'percentage';
      values.discount_value = 0;
    }
    delete values.discount;
    if (editingProduct) {
      updateProductMutation.mutate({ id: editingProduct.id, ...values });
    } else {
      createProductMutation.mutate(values);
    }
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'images',
      key: 'image',
      width: 90,
      render: (images, record) => {
        // Try multiple ways to get images
        const imageData = images || record.images || [];

        return (
          <div
            style={{
              width: 80,
              height: 60,
              borderRadius: '8px',
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              color: '#666',
              position: 'relative',
            }}
          >
            {imageData && imageData.length > 0 ? (
              <>
                <img
                  src={getStorageUrl(
                    typeof imageData[0] === 'string'
                      ? imageData[0]
                      : imageData[0].url || imageData[0].image_url,
                  )}
                  alt={record.name}
                  style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: '8px' }}
                  onError={(e) => {
                    e.target.src = '';
                    e.target.alt = record.name?.charAt(0)?.toUpperCase() || 'P';
                  }}
                />
                {imageData.length > 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      background: '#1890ff',
                      color: 'white',
                      borderRadius: '50%',
                      width: 16,
                      height: 16,
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    +{imageData.length - 1}
                  </div>
                )}
              </>
            ) : (
              <span>{record.name?.charAt(0)?.toUpperCase() || 'P'}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      ellipsis: true,
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{name}</div>
          {record.description && (
            <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>
              {record.description.length > 50
                ? `${record.description.substring(0, 50)}...`
                : record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      sorter: (a, b) => (a.type || '').localeCompare(b.type || ''),
      render: (type) => (
        <Tag color={type === 'product' ? 'blue' : 'green'}>{type.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: (a, b) => {
        const catA = typeof a.category === 'string' ? a.category : a.category?.name || '';
        const catB = typeof b.category === 'string' ? b.category : b.category?.name || '';
        return catA.localeCompare(catB);
      },
      render: (category) => (typeof category === 'string' ? category : category?.name || 'N/A'),
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      sorter: (a, b) => {
        const brandA = typeof a.brand === 'string' ? a.brand : a.brand?.name || '';
        const brandB = typeof b.brand === 'string' ? b.brand : b.brand?.name || '';
        return brandA.localeCompare(brandB);
      },
      render: (brand) => (typeof brand === 'string' ? brand : brand?.name || 'N/A'),
    },
    {
      title: 'Provider',
      key: 'provider',
      width: 130,
      render: (_, record) => {
        const provider = record.provider;
        return provider ? (
          <Space>
            <Tag color="purple">
              {provider.name || provider.full_name || provider.business_name}
            </Tag>
          </Space>
        ) : (
          <span style={{ color: '#999' }}>None</span>
        );
      },
    },
    {
      title: 'Selling Price',
      dataIndex: 'price',
      key: 'price',
      width: 110,
      sorter: (a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0),
      render: (price, record) => {
        const salePrice = record.sale_price != null ? parseFloat(record.sale_price) : null;
        if (salePrice != null && salePrice < parseFloat(price || 0)) {
          return (
            <Space direction="vertical" size={0}>
              <Text delete style={{ fontSize: 12, color: '#999' }}>
                Rs. {parseFloat(price || 0).toFixed(2)}
              </Text>
              <span style={{ color: '#52c41a', fontWeight: 600 }}>Rs. {salePrice.toFixed(2)}</span>
            </Space>
          );
        }
        return `Rs. ${parseFloat(price || 0).toFixed(2)}`;
      },
    },
    {
      title: 'Discount',
      dataIndex: 'discount_value',
      key: 'discount',
      width: 90,
      render: (_, record) => {
        const pct = record.discount_percentage;
        if (!pct || parseFloat(pct) <= 0) return <span style={{ color: '#999' }}>—</span>;
        return <Tag color="red">{parseFloat(pct).toFixed(0)}% OFF</Tag>;
      },
    },
    {
      title: 'Cost Price',
      dataIndex: 'cost_price',
      key: 'cost_price',
      width: 110,
      sorter: (a, b) => (parseFloat(a.cost_price) || 0) - (parseFloat(b.cost_price) || 0),
      render: (cost_price) => `Rs. ${parseFloat(cost_price || 0).toFixed(2)}`,
    },
    {
      title: 'Profit',
      key: 'profit',
      width: 120,
      sorter: (a, b) => {
        const saleA = parseFloat(a.sale_price ?? a.price) || 0;
        const saleB = parseFloat(b.sale_price ?? b.price) || 0;
        return saleA - (parseFloat(a.cost_price) || 0) - (saleB - (parseFloat(b.cost_price) || 0));
      },
      render: (_, record) => {
        const cost = parseFloat(record.cost_price) || 0;
        const price = parseFloat(record.sale_price ?? record.price) || 0;
        if (cost === 0 && price === 0) return <span style={{ color: '#999' }}>—</span>;
        if (cost === 0) return <Tag color="blue">Rs. {price.toFixed(2)}</Tag>;
        const profit = price - cost;
        const margin = (profit / price) * 100;
        const color = margin >= 20 ? 'green' : margin >= 10 ? 'orange' : 'red';
        return (
          <Space>
            <span>Rs. {profit.toFixed(2)}</span>
            <Tag color={color}>{margin.toFixed(0)}%</Tag>
          </Space>
        );
      },
    },
    {
      title: 'Stock',
      dataIndex: 'stock_quantity',
      key: 'stock_quantity',
      width: 140,
      sorter: (a, b) => (parseInt(a.stock_quantity) || 0) - (parseInt(b.stock_quantity) || 0),
      render: (stock, record) => {
        if (record.type === 'service') {
          return <Tag color="purple">Service</Tag>;
        }

        const stockStatus =
          record.stock_status ||
          (stock > 10 ? 'in_stock' : stock > 0 ? 'low_stock' : 'out_of_stock');

        const statusConfig = {
          in_stock: { color: 'green', text: `In Stock (${stock})` },
          low_stock: { color: 'orange', text: `Low Stock (${stock})` },
          out_of_stock: { color: 'red', text: 'Out of Stock' },
        };

        const config = statusConfig[stockStatus] || statusConfig.out_of_stock;

        return (
          <Space direction="vertical" size="small">
            <Tag color={config.color}>{config.text}</Tag>
            <Button
              size="small"
              icon={<StockOutlined />}
              onClick={() => handleStockManagement(record)}
            >
              Manage Stock
            </Button>
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_available',
      key: 'is_available',
      width: 100,
      sorter: (a, b) => (a.is_available ? 1 : 0) - (b.is_available ? 1 : 0),
      render: (isAvailable) => (
        <Tag color={isAvailable ? 'green' : 'red'}>{isAvailable ? 'Available' : 'Unavailable'}</Tag>
      ),
    },
    {
      title: 'Orders',
      key: 'orders',
      width: 90,
      sorter: (a, b) => (parseInt(a.orders_count) || 0) - (parseInt(b.orders_count) || 0),
      render: (_, record) => (
        <Popover
          content={
            <div>
              <div>
                Total Orders: <strong>{record.orders_count || 0}</strong>
              </div>
              <div>
                Unique Customers: <strong>{record.customers_count || 0}</strong>
              </div>
            </div>
          }
          title="Order Statistics"
        >
          <Statistic
            value={record.orders_count || 0}
            prefix={<ShoppingCartOutlined />}
            valueStyle={{ fontSize: '18px' }}
          />
        </Popover>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <Title level={2} style={{ marginBottom: '24px', color: '#262626' }}>
        Product Management
      </Title>

      {/* Filters */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
        styles={{
          body: { padding: '16px' },
        }}
      >
        <Space wrap size="middle">
          <Input.Search
            placeholder="Search products..."
            style={{ width: 250, minWidth: 200 }}
            value={searchTerm}
            onSearch={(value) => {
              setSearchTerm(value);
              setPage(1);
            }}
            onChange={(e) => !e.target.value && (setSearchTerm(''), setPage(1))}
          />
          <Select
            placeholder="Filter by category"
            style={{ width: 200, minWidth: 150 }}
            allowClear
            onChange={(value) => {
              setCategoryFilter(value);
              setPage(1);
            }}
          >
            {categories.map((cat) => (
              <Option key={cat.id} value={cat.id}>
                {cat.name}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Filter by type"
            style={{ width: 150, minWidth: 120 }}
            allowClear
            onChange={(value) => {
              setTypeFilter(value);
              setPage(1);
            }}
          >
            <Option value="product">Products</Option>
            <Option value="service">Services</Option>
          </Select>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
            Add Product
          </Button>
        </Space>
      </Card>

      {/* Products Table */}
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
        styles={{
          body: { padding: 0, overflowX: 'auto' },
        }}
      >
        <Table
          columns={columns}
          dataSource={filteredProducts}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: true }}
          pagination={{
            current: page,
            pageSize: perPage,
            total: totalProducts,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            style: { marginRight: 16, marginBottom: 16 },
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              setPerPage(newPageSize);
            },
            onShowSizeChange: (_, newPageSize) => {
              setPerPage(newPageSize);
              setPage(1);
            },
          }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingProduct ? 'Edit Product' : 'Add Product'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingProduct(null);
          setImageFileList([]);
          form.resetFields();
        }}
        footer={null}
        width={800}
        style={{ top: 20 }}
        styles={{
          body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' },
        }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: 'Please input product name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please input description!' }]}
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select type!' }]}
          >
            <Select>
              <Option value="product">Product</Option>
              <Option value="service">Service</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="category_id"
            label="Category"
            rules={[{ required: true, message: 'Please select category!' }]}
          >
            <Select>
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="brand_id" label="Brand">
            <Select allowClear>
              {brands.map((brand) => (
                <Option key={brand.id} value={brand.id}>
                  {brand.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="provider_id" label="Provider">
            <Select allowClear placeholder="Select provider (optional)">
              {(providers || []).map((provider) => (
                <Option key={provider.id} value={provider.id}>
                  {provider.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="price"
            label="Selling Price (Rs.)"
            rules={[{ required: true, message: 'Please input price!' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              formatter={(value) => `Rs. ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/Rs.\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item name="cost_price" label="Cost Price (Rs.)">
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              formatter={(value) => `Rs. ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/Rs.\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="stock_quantity"
            label="Stock Quantity"
            rules={[{ required: false, message: 'Please input stock quantity!' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>

          <Form.Item name="is_available" label="Available" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="discount" label="Discount (%)">
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              precision={2}
              placeholder="Enter discount percentage"
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace('%', '')}
            />
          </Form.Item>

          <Form.Item label="Product Images">
            <Upload
              name="images"
              listType="picture-card"
              multiple
              beforeUpload={() => false}
              accept="image/*"
              fileList={imageFileList}
              onChange={({ fileList }) => setImageFileList(fileList)}
              onRemove={(file) => {
                const newFileList = imageFileList.filter((f) => f.uid !== file.uid);
                setImageFileList(newFileList);
                return true;
              }}
            >
              {imageFileList.length >= 10 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
            <div style={{ color: '#999', fontSize: '12px', marginTop: 4 }}>
              Support multiple images. Max 10 images. First image will be the main image.
            </div>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createProductMutation.isLoading || updateProductMutation.isLoading}
              >
                {editingProduct ? 'Update' : 'Create'}
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingProduct(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Product Modal */}
      <Modal
        title="Product Details"
        open={isViewModalVisible}
        onCancel={() => {
          setIsViewModalVisible(false);
          setViewingProduct(null);
        }}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setIsViewModalVisible(false);
              handleEdit(viewingProduct);
            }}
          >
            Edit Product
          </Button>,
        ]}
        width={900}
        style={{ top: 20 }}
        styles={{
          body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' },
        }}
      >
        {viewingProduct && (
          <Row gutter={[16, 16]}>
            <Col span={8}>
              {viewingProduct.images && viewingProduct.images.length > 0 ? (
                <Image
                  width="100%"
                  src={getStorageUrl(
                    typeof viewingProduct.images[0] === 'string'
                      ? viewingProduct.images[0]
                      : viewingProduct.images[0].url || viewingProduct.images[0].image_url,
                  )}
                  style={{ borderRadius: '8px' }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: 200,
                    background: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    border: '1px solid #d9d9d9',
                  }}
                >
                  <span style={{ color: '#999' }}>No Image</span>
                </div>
              )}
            </Col>
            <Col span={16}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Title level={4}>{viewingProduct.name}</Title>
                  <Tag color={viewingProduct.type === 'product' ? 'blue' : 'green'}>
                    {viewingProduct.type?.toUpperCase()}
                  </Tag>
                  <Tag color={viewingProduct.is_available ? 'green' : 'red'}>
                    {viewingProduct.is_available ? 'Available' : 'Unavailable'}
                  </Tag>
                </div>

                <div>
                  <strong>Description:</strong>
                  <p>{viewingProduct.description || 'No description available'}</p>
                </div>

                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Selling Price"
                      value={viewingProduct.sale_price ?? viewingProduct.price}
                      prefix="Rs. "
                      precision={2}
                    />
                    {viewingProduct.sale_price != null && (
                      <div style={{ fontSize: 12, color: '#999' }}>
                        Marked: <s>Rs. {parseFloat(viewingProduct.price || 0).toFixed(2)}</s>
                      </div>
                    )}
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Cost Price"
                      value={viewingProduct.cost_price || 0}
                      prefix="Rs. "
                      precision={2}
                      valueStyle={{ color: '#eb2f96' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Category"
                      value={
                        typeof viewingProduct.category === 'string'
                          ? viewingProduct.category
                          : viewingProduct.category?.name || 'N/A'
                      }
                    />
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Brand"
                      value={
                        typeof viewingProduct.brand === 'string'
                          ? viewingProduct.brand
                          : viewingProduct.brand?.name || 'N/A'
                      }
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Provider"
                      value={
                        viewingProduct.provider?.name || viewingProduct.provider?.full_name || 'N/A'
                      }
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Col>
                  <Col span={8}>
                    {viewingProduct.type === 'product' ? (
                      <Statistic
                        title="Stock"
                        value={viewingProduct.stock_quantity || 0}
                        suffix="units"
                      />
                    ) : (
                      <Statistic title="Type" value="Service" valueStyle={{ color: '#722ed1' }} />
                    )}
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="Total Orders"
                      value={viewingProduct.orders_count || 0}
                      prefix={<ShoppingCartOutlined />}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Customers" value={viewingProduct.customers_count || 0} />
                  </Col>
                </Row>

                {viewingProduct.tags && viewingProduct.tags.length > 0 && (
                  <div>
                    <strong>Tags:</strong>
                    <div style={{ marginTop: '8px' }}>
                      {viewingProduct.tags.map((tag, index) => (
                        <Tag key={index} style={{ marginBottom: '4px' }}>
                          {tag}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </Space>
            </Col>
          </Row>
        )}
      </Modal>

      {/* Stock Management Modal */}
      <Modal
        title={`Manage Stock - ${stockProduct?.name}`}
        open={isStockModalVisible}
        onCancel={() => {
          setIsStockModalVisible(false);
          setStockProduct(null);
          stockForm.resetFields();
        }}
        footer={null}
        width={500}
        style={{ top: 100 }}
      >
        <Form form={stockForm} layout="vertical" onFinish={handleStockUpdate}>
          <Form.Item label="Current Stock">
            <InputNumber
              value={stockProduct?.stock_quantity || 0}
              disabled
              style={{ width: '100%' }}
              addonAfter="units"
            />
          </Form.Item>

          <Form.Item
            name="operation"
            label="Operation"
            rules={[{ required: true, message: 'Please select operation!' }]}
          >
            <Select>
              <Option value="set">Set Stock</Option>
              <Option value="add">Add Stock</Option>
              <Option value="subtract">Remove Stock</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[{ required: true, message: 'Please input quantity!' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} addonAfter="units" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={updateStockMutation.isLoading}>
                Update Stock
              </Button>
              <Button
                onClick={() => {
                  setIsStockModalVisible(false);
                  setStockProduct(null);
                  stockForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Products;
